import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useState } from 'react';

import { useAuthContext } from '../hooks/useAuthContext';

import Button from '../components/common/Button';
import Input from '../components/common/Input';

const { VITE_API_SERVICE } = import.meta.env;

export default function Login() {
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({
    email: '',
    password: ''
  });

  const { user, token, dispatch } = useAuthContext();
  const navigate = useNavigate();

  const {
    mutate: authenticateUser,
    isPending,
    reset: resetAuthentication
  } = useMutation({
    mutationFn: async (credentials) => {
      const response = await fetch(`${VITE_API_SERVICE}/v1/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(credentials)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message, {
          cause: data.errors
        });
      }

      return data;
    },
    onSuccess: (data) => {
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      toast.success('Login successful');

      dispatch({
        type: 'login',
        payload: {
          user: data.user,
          token: data.token
        }
      });

      navigate('/dashboard');
    },
    onError: (error) => {
      if (!error.cause) {
        return toast.error(error.message);
      }

      const { cause: validationErrors } = error;
      for (const validationError of validationErrors) {
        setErrors((prev) => ({
          ...prev,
          [validationError.path]: validationError.message
        }));
      }
    }
  });

  /**
   * Validates the login form
   * @returns {boolean} True if the form is valid, false otherwise
   */
  function validateForm() {
    const newErrors = { email: '', password: '' };

    if (!email) {
      newErrors.email = 'Email is required';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);

    return Object.values(newErrors).every((error) => !error);
  }

  /**
   * Handles the submission of the login form
   * @param {import('react').SyntheticEvent} e The event object
   */
  function handleSubmit(e) {
    e.preventDefault();

    resetAuthentication();

    if (validateForm()) {
      authenticateUser({ email, password });
    }
  }

  return (
    <div className="flex flex-col min-h-dvh">
      <div className="md:hidden bg-[url('/background.png')] bg-cover h-6"></div>

      <div className="flex justify-center md:justify-start items-center p-4 md:w-1/3">
        <img src="/logo.png" alt="Logo" />
      </div>

      <div className="flex-grow md:mt-28 px-6">
        <div className="mx-auto w-full max-w-md md:max-w-lg">
          <div className="md:bg-[url('/background.png')] bg-cover bg-center p-5 rounded-3xl">
            <div className="flex flex-col items-center gap-10 bg-white px-10 py-24 rounded-xl">
              <h1 className="font-body font-light" style={{ color: '#0094DA' }}>
                Login
              </h1>

              <form onSubmit={handleSubmit} className="w-full">
                <Input
                  icon="user"
                  className="px-4"
                  type="email"
                  placeholder="Email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={!!errors.email}
                  errorMessage={errors.email}
                />

                <div className="h-6" />

                <Input
                  icon="key"
                  className="px-4"
                  type="password"
                  placeholder="Password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={!!errors.password}
                  errorMessage={errors.password}
                />

                <div className="h-6" />

                <Button
                  type="submit"
                  value="Login"
                  isLoading={isPending}
                  loadingValue="Logging in..."
                />
              </form>
            </div>
          </div>
        </div>
      </div>

      <div className="md:hidden h-7" style={{ background: '#005076' }}></div>
    </div>
  );
}
